/**
 * Editorial section primitives — match the landing aesthetic.
 *
 *   <Section eyebrow="Today" title="Overview." description="…">
 *     <NumbersStrip items={…} />
 *     <YourContent />
 *   </Section>
 *
 *   <EmptyGrid label="No fee receipts this term." />
 *
 * Tokens used: --fg-high, --fg-mid, --fg-low, --bg-canvas, --bg-sunken,
 * --border-line, --border-hair, --color-accent-500/600.
 */
import { type CSSProperties, type ReactNode } from 'react';
import { Mark4x4 } from '../brand/brand.js';

/* ---------------------------------------------------------------------- */
/* Section                                                                 */
/* ---------------------------------------------------------------------- */

export interface SectionProps {
  id?: string;
  /** Small uppercase mono label above the title. */
  eyebrow?: string;
  title?: ReactNode;
  description?: ReactNode;
  /** Right-side actions (e.g. CTAs) shown next to the title block. */
  actions?: ReactNode;
  tone?: 'canvas' | 'sunken';
  /** Removes the outer max-width container — useful inside an app shell that already paginates. */
  bare?: boolean;
  /** Vertical padding scale. Default 'lg' matches the landing; 'sm' suits app screens. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const PADDING: Record<NonNullable<SectionProps['size']>, string> = {
  sm: 'py-6 md:py-8',
  md: 'py-10 md:py-12',
  lg: 'py-20 md:py-24',
};

export function Section({
  id,
  eyebrow,
  title,
  description,
  actions,
  tone = 'canvas',
  bare = false,
  size = 'md',
  className = '',
  style,
  children,
}: SectionProps) {
  const headerBlock = (eyebrow || title || description || actions) && (
    <div className="mb-7 flex items-start justify-between gap-6">
      <div className="max-w-2xl">
        {eyebrow && (
          <p
            className="text-[11px] font-semibold uppercase"
            style={{
              color: 'var(--color-accent-600)',
              letterSpacing: '0.10em',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {eyebrow}
          </p>
        )}
        {title && (
          <h2
            className="mt-2"
            style={{
              fontSize: size === 'lg' ? '2rem' : '1.375rem',
              lineHeight: size === 'lg' ? '2.4rem' : '1.75rem',
              fontWeight: 600,
              letterSpacing: '-0.024em',
              color: 'var(--fg-high)',
            }}
          >
            {title}
          </h2>
        )}
        {description && (
          <p
            className="mt-2 text-[13.5px] leading-relaxed"
            style={{ color: 'var(--fg-mid)' }}
          >
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );

  const container = bare ? '' : 'mx-auto max-w-6xl px-6';

  return (
    <section
      id={id}
      className={className}
      style={{
        background: tone === 'sunken' ? 'var(--bg-sunken)' : 'transparent',
        borderTop: tone === 'sunken' ? '1px solid var(--border-hair)' : undefined,
        borderBottom: tone === 'sunken' ? '1px solid var(--border-hair)' : undefined,
        ...style,
      }}
    >
      <div className={`${container} ${PADDING[size]}`}>
        {headerBlock}
        {children}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------- */
/* NumbersStrip — paced KPI row used in landing + dashboards.              */
/* ---------------------------------------------------------------------- */

export interface NumberItem {
  value: ReactNode;
  label: string;
  hint?: string;
}

export interface NumbersStripProps {
  items: NumberItem[];
  /** When true, wraps the strip in its own sunken band like the landing. */
  banded?: boolean;
  className?: string;
}

export function NumbersStrip({ items, banded = false, className = '' }: NumbersStripProps) {
  const inner = (
    <div className={`grid grid-cols-2 gap-y-7 md:grid-cols-${Math.min(items.length, 4)} md:gap-y-0 ${className}`}>
      {items.map((n, i) => (
        <div
          key={n.label}
          className="flex flex-col md:items-center md:text-center"
          style={
            i > 0
              ? {
                  borderLeft: '1px solid var(--border-hair)',
                  paddingLeft: '1.25rem',
                }
              : undefined
          }
        >
          <p
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              letterSpacing: '-0.028em',
              color: 'var(--fg-high)',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.05,
            }}
          >
            {n.value}
          </p>
          <p
            className="mt-1.5 text-[10.5px] uppercase"
            style={{
              color: 'var(--fg-low)',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {n.label}
          </p>
          {n.hint && (
            <p
              className="mt-1 text-[11px]"
              style={{ color: 'var(--fg-low)' }}
            >
              {n.hint}
            </p>
          )}
        </div>
      ))}
    </div>
  );

  if (!banded) return inner;
  return (
    <div
      className="border-y"
      style={{ borderColor: 'var(--border-hair)', background: 'var(--bg-sunken)' }}
    >
      <div className="mx-auto max-w-6xl px-6 py-8">{inner}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* EmptyGrid — empty state using the yantra-grid 'one cell lit' pattern.   */
/* ---------------------------------------------------------------------- */

export interface EmptyGridProps {
  /** Headline displayed beneath the mark. */
  label: string;
  /** Optional helper sentence. */
  hint?: string;
  /** Optional CTA — typically a button. */
  action?: ReactNode;
  className?: string;
}

export function EmptyGrid({ label, hint, action, className = '' }: EmptyGridProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-14 text-center ${className}`}
      style={{ color: 'var(--fg-low)' }}
    >
      <Mark4x4 size={56} accent={{ row: 2, col: 1 }} />
      <p
        className="mt-6 text-[14px] font-medium"
        style={{ color: 'var(--fg-high)', letterSpacing: '-0.012em' }}
      >
        {label}
      </p>
      {hint && (
        <p
          className="mt-1.5 max-w-sm text-[12.5px] leading-relaxed"
          style={{ color: 'var(--fg-low)' }}
        >
          {hint}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* GridDivider — 1×N strip divider that picks up the brand grid motif.     */
/* ---------------------------------------------------------------------- */

export interface GridDividerProps {
  /** Number of cells, default 24. */
  cells?: number;
  /** Index (0-based) of the lit emerald cell. Defaults to roughly 1/3 from the left. */
  accent?: number;
  className?: string;
}

export function GridDivider({ cells = 24, accent, className = '' }: GridDividerProps) {
  const accentCell = accent ?? Math.floor(cells / 3);
  return (
    <div
      className={`flex w-full gap-1 ${className}`}
      role="presentation"
      aria-hidden
    >
      {Array.from({ length: cells }, (_, i) => (
        <span
          key={i}
          className="flex-1 h-[6px] rounded-[2px]"
          style={{
            background: i === accentCell ? 'var(--color-accent-500)' : 'var(--border-line)',
          }}
        />
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* PageHero — large editorial heading used at the top of app screens.      */
/* ---------------------------------------------------------------------- */

export interface PageHeroProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Show the ambient yantra-grid backdrop motif (default: false on app screens). */
  backdrop?: boolean;
  className?: string;
}

export function PageHero({
  eyebrow,
  title,
  description,
  actions,
  backdrop = false,
  className = '',
}: PageHeroProps) {
  return (
    <header
      className={`relative isolate overflow-hidden ${className}`}
      style={{ background: 'transparent' }}
    >
      {backdrop && (
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-12 opacity-[0.04] dark:opacity-[0.07] hidden lg:block"
          style={{ color: 'var(--fg-high)' }}
        >
          <Mark4x4 size={220} accent={{ row: 2, col: 1 }} />
        </div>
      )}
      <div className="relative flex items-start justify-between gap-6 pb-8">
        <div className="max-w-2xl">
          {eyebrow && (
            <p
              className="text-[11.5px] font-semibold uppercase"
              style={{
                color: 'var(--color-accent-600)',
                letterSpacing: '0.12em',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {eyebrow}
            </p>
          )}
          <h1
            className="mt-3"
            style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.375rem)',
              lineHeight: 1.08,
              fontWeight: 600,
              letterSpacing: '-0.032em',
              color: 'var(--fg-high)',
            }}
          >
            {title}
          </h1>
          {description && (
            <p
              className="mt-3 max-w-xl text-[14.5px] leading-relaxed"
              style={{ color: 'var(--fg-mid)', letterSpacing: '-0.003em' }}
            >
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>
    </header>
  );
}
